import React, { useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PlayCircle } from 'lucide-react';
import ReactPlayer from 'react-player';
import { type ClassItem, type CurriculumItem } from '../../data/classes';
import {
    isOpenClassItem,
    classDetailDtoToClassItem,
    resolveCurriculumItemVideoUrl,
    isYoutubePlaybackUrl,
    getYouTubeVideoIdForEmbed,
    buildYoutubeIframeSrc,
} from '@/utils/classUtils';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { Button } from '@/components/shared/ui';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../lib/constants/routes';
import { ScenarioDetail } from '../scenario';
import { VideoLecture } from '../../types/curriculum';
import CurriculumSidebar from './components/CurriculumSidebar';
import { useClassDetail } from '@/data/queries/useClasses';
import { handleApiError } from '@/utils/errorHandlers';
import { ApiError } from '@/services/apiClient';

export default function ClassSession(): React.ReactElement {
    const { t } = useTranslation('class');
    const { classId: classIdParam, sessionId } = useParams<{ classId: string; sessionId: string }>();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const { getCurrentRole } = useAuth();

    const role = getCurrentRole();
    const isManagerPath = pathname.includes('class-management');
    const basePath = role === 'master' ? '/master' : '/admin';
    const currentTab = searchParams.get('tab') || 'curriculum';

    const classId = parseInt((classIdParam ?? '').trim(), 10);

    const {
        data: classDetailDto,
        isError,
        error: queryError,
        refetch,
    } = useClassDetail(classId, {
        enabled: classId > 0,
    });

    const classItem: ClassItem | null = useMemo(() => {
        if (classDetailDto) {
            try {
                return classDetailDtoToClassItem(classDetailDto);
            } catch {
                return null;
            }
        }
        return null;
    }, [classDetailDto]);

    /** 재조회·경합으로 URL이 잠깐 빠져도 플레이어가 사라지지 않게 직전 성공 URL 유지 */
    const videoUrlStickyRef = useRef<{ sid: string; url: string } | null>(null);
    /** YouTube는 URL 문자열보다 비디오 ID만 유지 (watch ↔ youtu.be 전환 시 ReactPlayer 깨짐 방지) */
    const youtubeIdStickyRef = useRef<{ sid: string; yid: string } | null>(null);

    const backToClassDetailPath = (): string => {
        if (isManagerPath) {
            return `${basePath}/class-management/${classId}`;
        }
        if (role === 'master') {
            return ROUTES.MASTER.CLASS_DETAIL(String(classId));
        }
        if (role === 'admin') {
            return ROUTES.ADMIN.CLASS_DETAIL(String(classId));
        }
        return `${ROUTES.STUDENT.BASE}/my-classes/${classId}`;
    };

    if (!classDetailDto && !isError) {
        return (
            <FullScreenLayout
                header={<FullScreenHeader title="…" onBack={() => navigate(backToClassDetailPath())} />}
            >
                <div className="p-8 text-gray-500">{t('myClass.loading')}</div>
            </FullScreenLayout>
        );
    }

    if (isError) {
        const fallback = t('detail.classNotFound');
        let message = fallback;
        if (queryError instanceof ApiError) {
            if (queryError.status === 401) {
                message = t('detail.classLoadAuthRequiredPrivate');
            } else if (queryError.status === 403) {
                message = t('detail.classLoadForbiddenPrivate');
            } else {
                message = handleApiError(queryError, fallback).message;
            }
        } else if (queryError instanceof Error) {
            message = handleApiError(queryError, fallback).message;
        }
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader title={t('shared.curriculum')} onBack={() => navigate(backToClassDetailPath())} />
                }
            >
                <div className="flex flex-col items-center justify-center gap-4 px-6 py-12 text-center text-gray-700">
                    <p>{message}</p>
                    <Button type="button" variant="outline" onClick={() => refetch()}>
                        {t('myClass.retry')}
                    </Button>
                </div>
            </FullScreenLayout>
        );
    }

    if (!classItem) {
        return <div className="p-8">{t('detail.classNotFound')}</div>;
    }

    const disableScenarioEditForMasterOpen = role === 'master' && isManagerPath && isOpenClassItem(classItem);
    // master는 class-management 커리큘럼 페이지에서 정보수정 불가
    const disableScenarioEdit = (role === 'master' && isManagerPath) || disableScenarioEditForMasterOpen;
    const showApiNotConnectedBadge = !classDetailDto;

    const sessionItem = classItem.curriculum.find((item: { id: number }) => item.id.toString() === sessionId);

    if (!sessionItem) {
        return <div className="p-8">{t('session.sessionNotFound')}</div>;
    }

    const sid = (sessionId ?? '').trim();
    const freshVideoUrl =
        sessionItem.type === 'video'
            ? resolveCurriculumItemVideoUrl(
                  sessionItem as CurriculumItem & { data?: unknown } & Record<string, unknown>
              )
            : null;
    if (videoUrlStickyRef.current && videoUrlStickyRef.current.sid !== sid) {
        videoUrlStickyRef.current = null;
    }
    if (freshVideoUrl) {
        videoUrlStickyRef.current = { sid, url: freshVideoUrl };
    }
    const videoUrl =
        freshVideoUrl ??
        (videoUrlStickyRef.current?.sid === sid ? videoUrlStickyRef.current.url : null);

    let youtubeId: string | null = null;
    if (videoUrl && isYoutubePlaybackUrl(videoUrl)) {
        youtubeId = getYouTubeVideoIdForEmbed(videoUrl);
    }
    if (youtubeIdStickyRef.current && youtubeIdStickyRef.current.sid !== sid) {
        youtubeIdStickyRef.current = null;
    }
    if (youtubeId) {
        youtubeIdStickyRef.current = { sid, yid: youtubeId };
    }
    const stableYoutubeId =
        youtubeId ?? (youtubeIdStickyRef.current?.sid === sid ? youtubeIdStickyRef.current.yid : null);

    const handleTabChange = (tab: string) => {
        if (tab === 'curriculum') {
            // 이미 커리큘럼 탭이므로 아무것도 안 함
            return;
        } else if (tab === 'evaluation') {
            // 평가결과 화면으로 이동
            navigate(`${basePath}/class-management/${classId}/evaluation`);
        } else if (tab === 'practice-status') {
            // 실습현황 화면으로 이동
            navigate(`${basePath}/class-management/${classId}/practice-status`);
        } else if (tab === 'statistics') {
            // 실습통계 화면으로 이동
            navigate(`${basePath}/class-management/${classId}/statistics`);
        } else if (tab === 'ranking') {
            // 랭킹 화면으로 이동
            navigate(`${basePath}/class-management/${classId}/ranking`);
        } else {
            // 다른 탭으로 이동
            navigate(`${basePath}/class-management/${classId}/session-management?tab=${tab}`);
        }
    };

    const tabs = [
        { id: 'curriculum', label: t('session.tabCurriculum') },
        { id: 'evaluation', label: t('session.tabEvaluation') },
        { id: 'practice-status', label: t('session.tabPracticeStatus') },
        { id: 'ranking', label: t('session.tabRanking') },
        { id: 'statistics', label: t('session.tabStatistics') },
    ];

    const renderCenterContent = () => {
        if (!isManagerPath) return null;

        return (
            <div className="flex gap-2">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            currentTab === tab.id
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    const renderRightContent = () => {
        if (!showApiNotConnectedBadge) return null;

        return (
            <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-semibold border border-red-300">
                {t('session.apiNotConnectedBadge')}
            </span>
        );
    };

    return (
        <FullScreenLayout
            header={
                <FullScreenHeader
                    title={classItem.title}
                    onBack={() => navigate(backToClassDetailPath())}
                    centerContent={renderCenterContent()}
                    rightContent={renderRightContent()}
                />
            }
        >

            <div className="flex flex-1 min-h-0">
                <CurriculumSidebar
                    classId={classId}
                    curriculum={classItem.curriculum}
                    activeSessionId={sessionId || null}
                    basePath={basePath}
                    isManagerPath={isManagerPath}
                />

                <div className="flex-1 bg-white overflow-y-auto">
                    {sessionItem.type === 'video' ? (
                        <div className="flex flex-col p-8">
                            <div className="w-full max-w-4xl mx-auto space-y-6">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{sessionItem.name}</h2>
                                </div>

                                <div
                                    className="relative w-full overflow-hidden rounded-xl bg-black shadow-2xl"
                                    style={{ paddingBottom: '56.25%' }}
                                >
                                    <div className="absolute inset-0">
                                        {(() => {
                                            if (videoUrl) {
                                                if (isYoutubePlaybackUrl(videoUrl)) {
                                                    if (stableYoutubeId) {
                                                        return (
                                                            <iframe
                                                                key={stableYoutubeId}
                                                                title={sessionItem.name}
                                                                className="block h-full w-full border-0"
                                                                src={buildYoutubeIframeSrc(stableYoutubeId)}
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                allowFullScreen
                                                                referrerPolicy="strict-origin-when-cross-origin"
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <div className="flex h-full w-full items-center justify-center px-4 text-center">
                                                            <p className="text-sm text-white/80">
                                                                {t('session.youtubeFormatError')}
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <ReactPlayer
                                                        key={sid}
                                                        url={videoUrl}
                                                        controls={true}
                                                        width="100%"
                                                        height="100%"
                                                        style={{ position: 'absolute', top: 0, left: 0 }}
                                                        className="react-player"
                                                        playing={false}
                                                    />
                                                );
                                            }
                                            return (
                                                <div className="flex h-full w-full items-center justify-center">
                                                    <div className="flex flex-col items-center gap-4 text-white opacity-50">
                                                        <PlayCircle size={64} />
                                                        <span className="text-xl font-medium">{t('session.videoUrlMissing')}</span>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                <div className="w-full space-y-4">
                                    {(() => {
                                        const videoData = (sessionItem as { data?: VideoLecture }).data as VideoLecture | undefined;
                                        const description =
                                            videoData?.description ||
                                            (sessionItem as { includes?: string }).includes ||
                                            (sessionItem as { description?: string }).description ||
                                            null;

                                        if (description) {
                                            return (
                                                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 whitespace-pre-wrap leading-relaxed">
                                                    <p className="text-gray-700">{description}</p>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-8 max-w-[1460px] mx-auto space-y-6">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">{sessionItem.name}</h2>
                                {sessionItem.practiceMode && (
                                    <p className="text-sm text-gray-600 mt-2">{sessionItem.practiceMode}</p>
                                )}
                            </div>
                            <ScenarioDetail
                                scenarioId={sessionItem.id.toString()}
                                hideBreadcrumbs={true}
                                disableScenarioEdit={disableScenarioEdit}
                            />
                        </div>
                    )}
                </div>
            </div>
        </FullScreenLayout>
    );
}
